import React, { useEffect, useState, useRef } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { useGlobalContext } from "../../../Config";
import Calendar from "../../../components/Calendar";
import { formatDate } from "@fullcalendar/core/index.js";
import CierreTable from "../components/CierreTable";
import { ResumenPanel } from "../components/panel";
import moment from "moment";
import Loader from "../../../shared/ui/Loader.js";
import { getAllMonedasByMonthYear } from "../../../services/CotizacionService.js";
import {
  getPeriodoFacturacionByPeriodo,
  getCabeceraCierre,
  ejecutarCierre,
  validarCierre,
  getDetalleCobroByIdPeriodoFacturacion,
  marcarEnvioAsignacion,
  guardarDetalleDocumento,
  exportCierreFacturacion,
  getEmpresas,
} from "../../../services/CierreService.js";

const Cierre = () => {
  const [filters2] = useState(null);
  const [displayBasic, setDisplayBasic] = useState(false);
  const [fila, setFila] = useState(null);
  const [producto, setProducto] = useState(null);
  const { baseUrl, urlMedidores, urlDocumentos, urlCobranzas } = useGlobalContext();
  const estados = ["A", "E"];
  const usuarioId = sessionStorage.getItem("usuario_id");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [cabecera, setCabecera] = useState(null);
  const [valorUfUsada, setValorUfUsada] = useState(null);
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [asignacionesCargadas, setAsignacionesCargadas] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [empresasMap, setEmpresasMap] = useState({});
  const [periodoOptions, setPeriodoOptions] = useState([]);
  const toast = useRef(null);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [monedaOptions, setMonedaOptions] = useState([]);
  const [monedaOptionsFetched, setMonedaOptionsFetched] = useState(false);
  const [primerDiaHabil, setPrimerDiaHabil] = useState(null);
  const [fechaVencimientoHabil, setFechaVencimientoHabil] = useState(null);
  const isMonedaServiceUnavailable =
    monedaOptionsFetched === false ||
    !Array.isArray(monedaOptions) ||
    monedaOptions.length === 0 ||
    !Number.isFinite(Number(valorUfUsada)) ||
    Number(valorUfUsada) <= 0;

  //---------------------------------
  // CABECERA POR EMPRESA
  //---------------------------------
  const cabeceraMap = {};
  (Array.isArray(cabecera) ? cabecera : []).forEach((c) => {
    if (c?.empresa) cabeceraMap[c.empresa] = c;
  });

  //---------------------------------
  //CARGA DE EMPRESAS DESDE LA API
  //---------------------------------
  const fetchEmpresas = async () => {
    try {
      const empresasData = await getEmpresas(baseUrl);
      const options = empresasData.map((e) => ({ label: e.nombre, value: e.id }));
      setEmpresas([...options, { label: "TODAS", value: empresasData.map((e) => e.id).join(",") }]);
      setEmpresasMap(Object.fromEntries(empresasData.map((e) => [e.nombre, e])));
      setSelectedEmpresa((prev) => prev ?? empresasData[0]?.id ?? null);
    } catch (error) {
      console.error("Error al obtener las empresas:", error);
    }
  };

  useEffect(() => {
    if (baseUrl) {
      fetchEmpresas();
    }
  }, [baseUrl]);

  //---------------------------------
  //CALCULO DE VALOR UF DEL MES SELECCIONADO
  //---------------------------------
  const fetchUfByPeriodo = async (periodo) => {
    const [year, month] = periodo.split("-");

    try {
      const data = await getAllMonedasByMonthYear(baseUrl, month, year);
      setMonedaOptions(data.monedas);
      setPrimerDiaHabil(data.primerDiaHabil ?? null);
      setFechaVencimientoHabil(data.fechaVencimiento ?? null);
      const monedaUf = data.monedas.find((m) => m.codigo === "UF");
      if (monedaUf) {
        setValorUfUsada(monedaUf.tipoCambio);
      } else {
        setValorUfUsada(null);
      }
      setMonedaOptionsFetched(true);
    } catch (error) {
      console.error("Error fetching UF data:", error);
      setValorUfUsada(null);
      setPrimerDiaHabil(null);
      setFechaVencimientoHabil(null);
      setMonedaOptions([]);
      setMonedaOptionsFetched(false);
    }
  };

  //---------------------------------
  // FORMATEO DE FECHAS HABILES
  //---------------------------------
  const formatFechaHabil = (fecha) =>
    fecha ? moment(fecha).format("DD-MM-YYYY") : null;

  const formatPeriodoLetras = (periodo) =>
    moment(periodo, "YYYY-MM")
      .locale("es")
      .format("MMMM [de] YYYY")
      .replace(/^\w/, (c) => c.toUpperCase());

  //---------------------------------
  // DATA FACTURACION DEL PERIODO
  //---------------------------------
  const fetchPeriodoFacturacion = async (periodo) => {
    if (!selectedEmpresa) return;
    setLoadingPeriodo(true);
    setAsignacionesCargadas(false);

    try {
      const result = await getPeriodoFacturacionByPeriodo(baseUrl, periodo, selectedEmpresa);
      setData(result.asignacionesPeriodo);
      setAsignacionesCargadas(true);
    } catch (error) {
      console.error("Error fetching periodo facturacion:", error);
    } finally {
      setLoadingPeriodo(false);
    }
  };

  //---------------------------------
  // OBTENER CABECERA DE CIERRE
  //---------------------------------
  const fetchCabecera = async (empresa, periodo) => {
    if (!empresa || !periodo) return;
    try {
      const result = await getCabeceraCierre(baseUrl, empresa, periodo);
      setCabecera(Array.isArray(result.cabeceras) ? result.cabeceras : []);
    } catch (error) {
      console.error("Error fetching cabecera:", error);
    }
  };

  //---------------------------------
  //GESTIÓN DE PERIODOS
  //---------------------------------
  function generatePeriodoOptions() {
    const months = [];
    const inicio = moment({ year: 2015, month: 0, day: 1 });
    const max = moment({ year: 2030, month: 11, day: 31 });
    let cursor = inicio.clone();
    while (cursor.isSameOrBefore(max, "month")) {
      const periodoValue = cursor.format("YYYY-MM");
      const periodoLabel = cursor
        .format("MMMM [de] YYYY")
        .replace(/^\w/, (c) => c.toUpperCase());
      months.push({ label: periodoLabel, value: periodoValue });
      cursor.add(1, "month");
    }

    setPeriodoOptions(months);
}

  //---------------------------------  
  //CARGA INICIAL DE DATOS
  //---------------------------------
  const [selectedPeriodo, setSelectedPeriodo] = useState(() => {const currentPeriodo = moment().format("YYYY-MM");return currentPeriodo;});
  const selectedPreviousPeriodo = moment(selectedPeriodo, "YYYY-MM").subtract(1, "month").format("YYYY-MM");
  const handleFetchAllData = async () => {
    setLoading(true);
    setLoadingPeriodo(true);
    setAsignacionesCargadas(false);
    try {
      await Promise.all([
        fetchPeriodoFacturacion(selectedPreviousPeriodo),
        fetchUfByPeriodo(selectedPeriodo),
        fetchCabecera(selectedEmpresa, selectedPeriodo),
        generatePeriodoOptions(),
      ]);
      setAsignacionesCargadas(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setLoadingPeriodo(false);
    }
  };

  //---------------------------------  
  // EFECTO SECUNDARIO
  //---------------------------------
  useEffect(() => {
    if (selectedEmpresa && selectedPeriodo) {
      handleFetchAllData();
    }
  }, [selectedEmpresa, selectedPeriodo]);

  //---------------------------------  
  // FORMATOS Y ESTILOS
  //---------------------------------
  const desdeBodyTemplate = (rowData) => {
    return formatDate(rowData.desde);
  };

  const hastaBodyTemplate = (rowData) => {
    return formatDate(rowData.hasta);
  };

  const estadoBodyTemplate = (rowData) => {
    return (
      <span className={`customer-badge status-${rowData.estado}`}>
        {rowData.estado}
      </span>
    );
  };

  const estadoFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={estados}
        onChange={(e) => options.filterCallback(e.value, options.index)}
        itemTemplate={statusItemTemplate}
        placeholder="Select a Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const statusItemTemplate = (option) => {
    return <span className={`customer-badge status-${option}`}>{option}</span>;
  };

  const esAsignacionPadre = (rowData) =>
    data?.some((a) => a?.folio && a.folio.startsWith(`${rowData?.idCotizacion}-`));

  const actionBodyTemplate = (rowData) => {
    if (esAsignacionPadre(rowData)) {
      return (
        <div style={{textAlign: "center" }}>
          <Button
            icon="pi pi-lock"
            className="p-button-rounded p-button-secondary p-button mr-2 mb-2 mt-2"
            disabled
            outlined
            tooltip="Histórico - solo referencia"
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      );
    }
    return (
      <div style={{textAlign: "center" }}>
        {!rowData.cerrada ? (
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-danger p-button mr-2 mb-2 mt-2"
            onClick={() => {}}
            outlined
            tooltip="Sin Mediciones"
            tooltipOptions={{ position: 'top' }}
          />
        ) : (
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-success p-button mr-2 mb-2 mt-2"
            outlined
            tooltip="Documento Completo"
            tooltipOptions={{ position: 'top' }}
          />
        )}
      </div>
    );
  };

  const buttonTemplate = (rowData) => {
    return (
      <div style={{textAlign: "center" }}>
        <Button
          // label="Reproceso"
          icon="pi pi-refresh"
          className="p-button p-button-rounded"
          style={{
            backgroundColor: "#66BB6A",
            borderColor: "#66BB6A",
            color: "white",
          }}
          disabled
          onClick={() => {}}
          outlined
          tooltip="Reprocesar"
          tooltipOptions={{ position: 'top' }}
          raised
        />
        
      </div>
    );
  };

  const dateFilterTemplate = (options) => {
    return (
      <Calendar
        value={options.value}
        onChange={(e) => options.filterCallback(e.value, options.index)}
        dateFormat="mm/dd/yy"
        placeholder="mm/dd/yyyy"
        mask="99/99/9999"
      />
    );
  };

  //---------------------------------
  //BOTON EJECUTAR CIERRE
  //---------------------------------
  const sumTotal = data?.reduce((acc, item) => acc + item.total, 0) ?? 0;
  const handleEjecutarCierre = async () => {
    const confirmation = window.confirm(
      "¿Está seguro que desea ejecutar el cierre? (UF utilizada: " +
        valorUfUsada +
        " - " +
        periodoOptions.find((p) => p.value === selectedPeriodo).label +
        ")"
    );

    if (!confirmation) {
      return; // Exit the function if the user cancels
    }

    try {
      const data = await ejecutarCierre(baseUrl, {
        empresa: selectedEmpresa,
        idUsuario: usuarioId,
        periodo: selectedPreviousPeriodo,
        valorUf: valorUfUsada,
      });
      const detalleEmpresas = Array.isArray(data.resultados)
        ? "\n" +
          data.resultados
            .map((r) => `${r.empresa}: ${r.respuesta ? "OK" : r.mensaje || "ERROR"}`)
            .join("\n")
        : "";
      if (data.respuesta) {
        alert("Cierre ejecutado correctamente: " + data.mensaje + detalleEmpresas);
      } else {
        alert("Error al ejecutar el cierre: " + data.mensaje + detalleEmpresas);
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      // Refresca asignaciones y cabecera (estado, ejecutor, validador) sin recargar la página
      handleFetchAllData();
    }
  };

  //---------------------------------
  //BOTON VALIDAR CIERRE
  //---------------------------------
  const handleValidarCierre = async () => {
    const confirmation = window.confirm(
      "¿Desea proceder con la validación del cierre de las asignaciones?"
    );

    if (!confirmation) {
      return; // Exit the function if the user cancels
    }

    try {
      const data = await validarCierre(baseUrl, {
        empresa: selectedEmpresa,
        idUsuario: usuarioId,
        periodo: selectedPreviousPeriodo,
      });
      const detalleEmpresas = Array.isArray(data.resultados)
        ? "\n" +
          data.resultados
            .map((r) => `${r.empresa}: ${r.respuesta ? "OK" : r.mensaje || "ERROR"}`)
            .join("\n")
        : "";
      if (data.respuesta) {
        alert("Cierre validado correctamente: " + data.mensaje + detalleEmpresas);
      } else {
        alert("Error al validar el cierre: " + data.mensaje + detalleEmpresas);
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      // Refresca asignaciones y cabecera (estado, validador) in-place, sin window.location.reload()
      handleFetchAllData();
    }
  };

  //---------------------------------
  //BOTON REPROCESO
  //---------------------------------
  const handleReproceso = async () => {};

  //---------------------------------
  //BOTON PUBLICAR CIERRE
  //---------------------------------
  const handlePublicarCierre = async () => {
    if (!Number.isFinite(Number(valorUfUsada)) || !primerDiaHabil || !fechaVencimientoHabil) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudieron determinar las fechas hábiles o el valor UF del período. Reintenta más tarde.",
        life: 5000,
      });
      return;
    }

    const count = data.filter(item => item.cerrada === true && item.enviado === 0 && item.ejecutado === 1).length;
    const count1 = data.filter(item => item.cerrada  === false).length;
    const count2 = data.filter(item => item.enviado === 1).length;
    const count3 = data.filter(item => item.ejecutado === 0).length;
    
    const confirmation = window.confirm(
      "¿Desea proceder con el envío masivo de las asignaciones a documentos?\n" +
      "Se enviarán solo las asignaciones con cierre aprobados (marcadas como ✓) son " + count + "\n" +
      "No se enviarán las asignaciones sin cierre, es decir, sin mediciones (marcadas como x) son: " + count1 + "\n" + 
      "No se enviarán las asignaciones que ya fueron enviadas son: " + count2 + "\n" +
      "No se enviarán las asignaciones sin ejecución del cierre (debe ejecutar el cierre antes de enviar) son: " + count3
    );

    if (!confirmation) {
      return; 
    }

    try {
      setLoading(true);
      
      // Filtrar asignaciones que no han sido enviadas y cuyo cierre fue ejecutado
      const asignacionesPendientes = data.filter(item => item.cerrada === true && item.enviado === 0 && item.ejecutado === 1);
      if (asignacionesPendientes.length === 0) {
        alert("No hay asignaciones pendientes para enviar.");
        setLoading(false);
        return;
      }

      // Procesar cada asignación y crear el payload masivo
      const payloadMasivo = [];
      
      for (const rowData of asignacionesPendientes) {

        // Resolver la empresa (rut) y la cabecera de cierre correspondiente a la asignación
        const empresaAsignacion = empresasMap[rowData.empresa];
        if (!empresaAsignacion?.rut) {
          console.error(`No se encontró la empresa "${rowData.empresa}" para la asignación ${rowData.id}; se omite.`);
          continue;
        }

        // Obtener detalles de cobro
        try {
          const detalleData = await getDetalleCobroByIdPeriodoFacturacion(baseUrl, rowData.id);

          // Generar productos del payload
          const productos = detalleData.detallesCobro.map((detalle) => ({
            docCobro: detalle.docCobro,
            item: detalle.servicios === "arriendo" ? `Arriendo - ${detalle.item}` : detalle.item,
            total: parseFloat(
              (
                (detalle.item.includes("Agua") || detalle.item.includes("Electricidad")) &&
                parseFloat(detalle.consumo) > 0
                  ? parseFloat(detalle.totalClp) / parseFloat(detalle.consumo)
                  : parseFloat(detalle.totalClp)
              ).toFixed(2)
            ),
            cantidad: detalle.item.includes("Agua") || detalle.item.includes("Electricidad")
              ? detalle.consumo
              : 1,
            unidadMedida: detalle.unidadMedida,
            lectInicial: detalle.lectInicial,
            lectFinal: detalle.lectFinal,
            consolidado: detalle.consolidado,
            agrupamiento: detalle.agrupamiento
          }));

          // Agregar al payload masivo
          payloadMasivo.push({
            descripcionContrato: rowData.folio ? `${rowData.folio}` : `${rowData.idCotizacion}`,
            asignacion: `${rowData.idCotizacion}`,
            rut: empresaAsignacion.rut,
            rutFuncionario: empresaAsignacion.rut,
            empresa: rowData.empresa,
            documento: { productos },
            periodo: moment(selectedPeriodo).format("YYYY-MM"),
            periodoLetras: formatPeriodoLetras(selectedPeriodo),
            fechaEmision: formatFechaHabil(primerDiaHabil),
            fechaVencimiento: formatFechaHabil(fechaVencimientoHabil),
            valorUf: valorUfUsada,
            fechaValorUF: formatFechaHabil(primerDiaHabil),
            rutContraparte: rowData.cliente.rut,
            razonSocialContraparte: rowData.cliente.razonSocial?.trim() ?? null,
            aliasContraparte: rowData.cliente.alias?.trim() ?? null,
            giroContraparte: rowData.cliente.giro,
            comunaContraparte: rowData.domicilios[0]?.comuna
              ? rowData.domicilios[0].comuna
              : "No tiene comuna",
            codigoSucursal: 1,
            primer_mes: rowData.primer_mes,
          }
        );
        } catch (error) {
          console.error(`Error al obtener detalles para asignación ${rowData.id}:`, error);
          continue;
        }
      }

      //console.log("Payload masivo generado:", payloadMasivo);

      if (payloadMasivo.length === 0) {
        alert("No hay asignaciones válidas para enviar.");
        setLoading(false);
        return;
      }

      // Enviar payload masivo
      const response = await guardarDetalleDocumento(urlDocumentos, payloadMasivo);

      if (response.ok) {
        // Marcar todas las asignaciones como enviadas
        for (const rowData of asignacionesPendientes) {
          await marcarEnvioAsignacion(baseUrl, rowData.id, sessionStorage.getItem("usuario_id"));
        }
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: `Se enviaron las asignaciones a documentos correctamente.`,
          life: 5000,
        });
      } else {
        const errorMessage = await response.text();
        alert(`Error al enviar las asignaciones: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error en el proceso de envío masivo:", error);
      alert("Ocurrió un error durante el proceso de envío masivo.");
      
    } finally {
      setLoading(false);
      handleFetchAllData();
    }
  };

  //---------------------------------
  // FUNCION PARA FORMATEAR NUMEROS
  //---------------------------------
  const formatNumber = (amount) => {
    return new Intl.NumberFormat("es-CL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  //---------------------------------
  //DESCARGAR EXCEL
  //---------------------------------
  const handleDescargaExcel = async () => {
      if (!data || data.length === 0) {
          alert("No hay datos para descargar.");
          return;
      }

      setExporting(true);
      try {
          await exportCierreFacturacion(baseUrl, {
              periodo: selectedPreviousPeriodo,
              empresa: selectedEmpresa,
              //empresa: false,
              valorUf: valorUfUsada,
          });
      } catch (error) {
          console.error("Error al exportar Excel:", error);
          alert("Ocurrió un error al exportar el Excel.");
      } finally {
          setExporting(false);
      }
  };

  //---------------------------------
  //TOTALES y CONTADORES DE CATEGORIAS Y DOCUMENTOS EN ENCABEZADO
  //---------------------------------
  //DOCUMENTOS
  const totalFacturaExenta = data?.reduce((acc, item) => acc + (item.detalles_total?.f_exenta || 0), 0);
  const totalFacturaAfecta = data?.reduce((acc, item) => acc + (item.detalles_total?.f_afecta || 0), 0);
  const totalNotaCobro = data?.reduce((acc, item) => acc + (item.detalles_total?.n_cobro || 0), 0);

  const cantFacturaExenta = data?.reduce((acc, item) => acc + (item.detalles_total?.cant_factura_exenta || 0), 0);
  const cantFacturaAfecta = data?.reduce((acc, item) => acc + (item.detalles_total?.cant_factura_afecta || 0), 0);
  const cantNotaCobro = data?.reduce((acc, item) => acc + (item.detalles_total?.cant_nota_cobro || 0), 0);

  //totales por categoria
  const totalArriendo = data?.reduce((acc, item) => {
    const uf = item.detalles?.arriendo_uf || 0
    const clp = (item.detalles?.arriendo_clp || 0) / (valorUfUsada || 1)
    return acc + (uf + clp);
  }, 0) || 0;

  const totalComun = data?.reduce((acc, item) => {
    const uf = item.detalles?.gasto_comun_uf || 0
    const clp = (item.detalles?.gasto_comun_clp || 0) / (valorUfUsada || 1)
    return acc + (uf + clp);
  }, 0) || 0;

  const totalCivil = data?.reduce((acc, item) => {
    const uf = item.detalles?.obra_uf || 0
    const clp = (item.detalles?.obra_clp || 0) / (valorUfUsada || 1)
    return acc + (uf + clp);
  }, 0) || 0;

  const totalServicio = data?.reduce((acc, item) => {
    const uf = item.detalles?.servicio_uf || 0
    const clp = (item.detalles?.servicio_clp || 0) / (valorUfUsada || 1)
    return acc + (uf + clp);
  }, 0) || 0;

  //contadores por categoria
  const cantComun = data?.reduce((acc, item) => acc + (item.detalles?.cant_gasto_comun || 0), 0) || 0;
  const cantServicio = data?.reduce((acc, item) => acc + (item.detalles?.cant_servicio || 0), 0) || 0;
  const cantCivil = data?.reduce((acc, item) => acc + (item.detalles?.cant_obra_civil || 0), 0) || 0;
  const cantArriendo = data?.reduce((acc, item) => acc + (item.detalles?.cant_arriendo || 0), 0) || 0;
  //---------------------------------
  //DESPLIEGUE DE COMPONENTES
  //---------------------------------
  const estiloTexto = {display: "block",fontSize: "1rem",color: "#333", marginBottom: "0.5rem",};

  return (
    <>

      <Card>
        <h2 style={{textAlign: "center"}}>
          Ciclo de cierre
        </h2>

        {/*<StepByStep activeIndex={activeIndex} items={items}></StepByStep>*/}
        {/* totales de cabecera */}
        <div className="p-fluid formgrid grid mt-5">
          <div className="field col-12 md:col-6 flex align-items-center">
            <Dropdown
              id="empresa"
              value={selectedEmpresa}
              options={empresas}
              onChange={async (e) => {
                setSelectedEmpresa(e.value);
              }}
              style={{ width: "48%", marginRight: "2%" }}
              placeholder="Seleccione Empresa"
              disabled={loading}
            />
            <Dropdown
              value={selectedPeriodo}
              options={periodoOptions}
              onChange={(e) => setSelectedPeriodo(e.value)}
              style={{ width: "48%" }}
              placeholder="Seleccione Periodo"
              disabled={loadingPeriodo || loading }
            />
          </div>
          <div className="field col-12 md:col-6">
            <h5 style={{display: "block",fontSize: "1.2rem",color: "#333",marginBottom: "0.5rem",}}>
              Estado:{" "}
              <span style={{ color: "red" }}>
                {Array.isArray(cabecera) && cabecera.length > 1
                  ? cabecera.map((c) => `${c.empresa}: ${c.estado || "pendiente"}`).join(" | ")
                  : Array.isArray(cabecera) && cabecera.length === 1
                    ? cabecera[0]?.estado || "pendiente"
                    : "pendiente"}
              </span>
            </h5>
            <h4 style={{ margin: "0.5rem 0" }}>
              Recaudación esperada del periodo: {formatNumber(sumTotal) || "0,00"} UF
            </h4>
          </div>
        </div>

        {/* div de botones */}
        <div className="formgrid grid mt-3">
            <div className="field col-12 md:col-3">
              <Button
                label="Ejecutar Cierre"
                icon={"pi pi-check"}
                style={{ width: "100%"}}
                onClick={handleEjecutarCierre}
                disabled={loading || isMonedaServiceUnavailable}
                raised
                tooltipOptions={{ position: "top" }}
                tooltip="Sincroniza todos los items del cierre con el valor UF del periodo"              
              />
            </div>
            <div className="field col-12 md:col-3">
              <Button
                label="Validar Cierre"
                icon="pi pi-check-circle"
                style={{ width: "100%"}}
                onClick={handleValidarCierre}
                disabled={loading || isMonedaServiceUnavailable}
                raised
                tooltipOptions={{ position: "top" }}
                tooltip="Marca el periodo indicando que ha sido validado por un usuario y que puede ser enviado a documentos"
              />
            </div>
            <div className="field col-12 md:col-3">
              <Button
                label="Generar Doc. Venta"
                icon="pi pi-upload"
                style={{ width: "100%"}}
                onClick={handlePublicarCierre}
                disabled={loading || isMonedaServiceUnavailable}
                raised
                tooltipOptions={{ position: "top" }}
                tooltip="Envia masivamente todos los items con cierre completo a documento de venta para la documentos"
              />
            </div>
            <div className="field col-12 md:col-3">
              <Button
                label="Ejecutar Reproceso"
                icon="pi pi-refresh"
                style={{ width: "100%"}}
                onClick={handleReproceso}
                disabled
                raised
              />
            </div>
        </div>
      </Card>
      
      {loading ? (
        <Loader />
      ) : (
        <ResumenPanel
          estiloTexto={estiloTexto}
          cabecera={cabecera}
          asignacionesCargadas={asignacionesCargadas}
          formatNumber={formatNumber}
          totalServicio={totalServicio}
          cantServicio={cantServicio}
          totalFacturaExenta={totalFacturaExenta}
          cantFacturaExenta={cantFacturaExenta}
          totalComun={totalComun}
          cantComun={cantComun}
          totalNotaCobro={totalNotaCobro}
          cantNotaCobro={cantNotaCobro}
          totalCivil={totalCivil}
          cantCivil={cantCivil}
          totalArriendo={totalArriendo}
          totalFacturaAfecta={totalFacturaAfecta}
          valorUfUsada={valorUfUsada}
          cantArriendo={cantArriendo}
          cantFacturaAfecta={cantFacturaAfecta}
        />
      )}
      
      {loadingPeriodo || !asignacionesCargadas ? (
          <Loader />  
        ) : data && data.length > 0 ? (
          <>
            <Card>
              <div className="p-col" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h5 style={{ marginBottom: "0" }}>Asignaciones</h5>
                <Button
                  label="Exportar Excel"
                  icon="pi pi-download"
                  className="p-button-rounded p-button-success"
                  style={{ marginLeft: "1rem" }}
                  onClick={handleDescargaExcel}
                  loading={exporting}
                  disabled={loading || exporting || isMonedaServiceUnavailable}
                  tooltip="Exportar Excel"
                  tooltipOptions={{ position: "top" }}
                  severity="success"
                />
              </div>
              <div className="p-col">
                <span>
                  Ordenadas por defecto desde la más reciente a la más antigua.
                </span>
              </div>
              <CierreTable
                asignaciones={data}
                filters={filters2}
                loading={loading}
                desdeBodyTemplate={desdeBodyTemplate}
                dateFilterTemplate={dateFilterTemplate}
                hastaBodyTemplate={hastaBodyTemplate}
                estadoBodyTemplate={estadoBodyTemplate}
                estadoFilterTemplate={estadoFilterTemplate}
                actionBodyTemplate={actionBodyTemplate}
                setDisplayBasic={setDisplayBasic}
                displayBasic={displayBasic}
                fila={fila}
                setFila={setFila}
                producto={producto}
                setProducto={setProducto}
                baseUrl={baseUrl}
                urlMedidores={urlMedidores}
                monedaOptions={monedaOptions}
                valorUfUsada={valorUfUsada}
                isMonedaServiceUnavailable={isMonedaServiceUnavailable}
                buttonTemplate={buttonTemplate}
                currentPeriodo={selectedPeriodo}
                handleFetchAllData={handleFetchAllData}
                setLoading={setLoading}
                setData={setData}
                selectedEmpresa={selectedEmpresa}
                urlDocumentos={urlDocumentos}
                urlCobranzas={urlCobranzas}
                 primerDiaHabil={primerDiaHabil}
                fechaVencimientoHabil={fechaVencimientoHabil}
                periodoLetras={formatPeriodoLetras(selectedPeriodo)}
                 empresasMap={empresasMap}
                 cabeceraMap={cabeceraMap}
                 previousPeriodo={selectedPreviousPeriodo}
                periodoDeCobro={selectedPeriodo}
                toast={toast}
              />
            </Card>
          </>
      ) : (
        <p>No hay asignaciones para este período.</p>
      )}
      <Toast ref={toast} />
    </>
  );
};

export default Cierre;
